import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, Sparkles, ArrowLeft, ArrowRight, ChevronDown } from 'lucide-react';
import { appUrl } from '../utils/appUrl';

/**
 * Minimal floating shell that replaces the old 9-item header.
 *
 * Strategy (per Feb 2026 conversion teardown):
 *   - The old nav had Home / App / Blog / Portals / Shop / Pricing / Clinicians /
 *     Partners / Contact + Go-to-App. That's option paralysis for a buyer.
 *   - Cross-site navigation now lives in the comprehensive Footer (Product /
 *     Learn / Conditions / Company columns).
 *   - The homepage's hub cards section acts as the visual sitemap for organic
 *     visitors who arrive on /.
 *   - This shell exposes ONLY the two decisions that matter at any moment:
 *       1. "See Plans" → recalibratepain.app/pricing (buyer intent, with UTM)
 *       2. "Open App"  → recalibratepain.app/        (returning user intent)
 *   - The logo top-left is a quiet way home from any internal page.
 *
 * Visual behaviour:
 *   - Transparent over the homepage hero (which has its own dark gradient).
 *   - Solid purple-glass pill on scroll, and on every non-home route.
 *   - The pills are kept small so they never compete with in-page hero CTAs.
 */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the slide-out menu on route change or Escape.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Solid background once scrolled or whenever the user is not on the home page.
  const solid = scrolled || !isHome;

  // Campaign tag = page bucket so .app/pricing analytics can group inbound
  // clicks by where they came from on recalibratepain.com.
  const campaign = isHome ? 'home' : location.pathname.replace(/^\//, '').split('/')[0] || 'home';

  const menuItems = [
    { label: 'Home', to: '/' },
    { label: 'Features', to: '/features' },
    { label: 'Blog', to: '/blog' },
    { label: 'Health Portals', to: '/portals' },
    { label: 'Shop', to: '/shop' },
    { label: 'For Clinicians', to: '/clinicians' },
    { label: 'Partners', to: '/partners' },
    { label: 'Pricing', to: '/pricing' },
    { label: 'Open the App', to: appUrl('/', 'shell-menu-open-app', campaign), external: true },
  ];

  return (
    <header
      data-testid="floating-shell"
      className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
    >
      <div className="w-full px-3 sm:px-4 pt-3 sm:pt-4">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          {/* ===== Brand pill with attached arrow menu (on every page) =====
               Home: logo + "Recalibrate". Other routes: back-arrow + "Home".
               Either way the wordmark links home and the arrow opens full nav. */}
          <div className="relative pointer-events-auto">
            <div
              className={`inline-flex items-center rounded-2xl transition-all duration-300 ${
                solid
                  ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-purple-900/10 border border-white/60'
                  : 'bg-white/10 backdrop-blur-md border border-white/15'
              }`}
            >
              <Link
                to="/"
                data-testid="shell-logo"
                aria-label={isHome ? 'Recalibrate home' : 'Back to home'}
                className="group inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-l-2xl"
              >
                {isHome ? (
                  <img
                    src="/recalibrate-logo-optimized.png"
                    alt="Recalibrate"
                    className="h-7 w-7 rounded-lg"
                    width="28"
                    height="28"
                    fetchpriority="high"
                  />
                ) : (
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-lg transition-all group-hover:-translate-x-0.5 ${
                      solid ? 'bg-slate-900 text-white' : 'bg-white/20 text-white border border-white/25'
                    }`}
                    data-testid="shell-back-arrow"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </span>
                )}
                <span
                  className={`${isHome ? 'hidden sm:inline' : ''} text-sm font-extrabold tracking-tight transition-colors ${solid ? 'text-slate-900' : 'text-white'}`}
                  data-testid="shell-brand-label"
                >
                  {isHome ? 'Recalibrate' : 'Home'}
                </span>
              </Link>
              <span className={`w-px h-5 ${solid ? 'bg-slate-900/10' : 'bg-white/20'}`} aria-hidden="true"></span>
              <button
                type="button"
                data-testid="shell-menu-toggle"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
                className={`inline-flex items-center justify-center w-9 h-9 mx-0.5 rounded-xl transition-all duration-300 ${
                  menuOpen
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md shadow-purple-600/30'
                    : solid ? 'text-slate-700 hover:bg-slate-900/5' : 'text-white hover:bg-white/10'
                }`}
              >
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${menuOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {menuOpen && (
              <>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMenuOpen(false)}
                  className="fixed inset-0 z-[-1] cursor-default"
                  data-testid="shell-menu-backdrop"
                  tabIndex={-1}
                />
                <div
                  data-testid="shell-menu-panel"
                  className="shell-menu-panel absolute left-0 top-full mt-2 w-60 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/10 shadow-2xl shadow-purple-900/40 p-2"
                >
                  {menuItems.map((item, i) => {
                    const props = item.external
                      ? { href: item.to, target: '_blank', rel: 'noopener noreferrer' }
                      : { to: item.to };
                    const Cmp = item.external ? 'a' : Link;
                    const active = !item.external && location.pathname === item.to;
                    return (
                      <Cmp
                        key={item.label}
                        {...props}
                        data-testid={`shell-menu-item-${i}`}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => setMenuOpen(false)}
                        className={`shell-menu-item flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                          active
                            ? 'bg-gradient-to-r from-blue-500/90 to-purple-600/90 text-white shadow-md shadow-purple-900/30'
                            : 'text-white/85 hover:text-white hover:bg-white/10'
                        }`}
                        style={{ animationDelay: `${i * 45}ms` }}
                      >
                        <span>{item.label}</span>
                        <ArrowRight className={`w-3.5 h-3.5 transition-all ${active ? 'opacity-100 translate-x-0.5' : 'opacity-40'}`} />
                      </Cmp>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* ===== Action cluster ===== */}
          <div className="pointer-events-auto flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Primary: low-friction free entry → .app root freemium signup.
                Cold SEO/lead traffic needs a "start free" step before the
                paid ask, which was the funnel gap. */}
            <a
              href={appUrl('/', 'shell-start-free', campaign)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shell-start-free"
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 sm:px-4 py-2 rounded-2xl text-xs sm:text-sm font-bold shadow-lg shadow-purple-600/25 hover:shadow-xl hover:shadow-purple-600/40 hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Start Free</span>
            </a>

            {/* Secondary: buyer-intent CTA → .app/pricing for warmer traffic.
                Hidden on very narrow phones so the bar never overflows - the
                free entry stays visible at every width. */}
            <a
              href={appUrl('/pricing', 'shell-see-plans', campaign)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shell-see-plans"
              className={`hidden min-[420px]:inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-2xl text-xs sm:text-sm font-bold transition-all hover:-translate-y-0.5 whitespace-nowrap ${
                solid
                  ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-md'
                  : 'bg-white/95 text-slate-900 hover:bg-white shadow-md backdrop-blur-md'
              }`}
            >
              <span>See Plans</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
